'use client';

import React, { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import type { Section, StepConfig, FormInstance } from '../../types';
import type { SectionStatus } from '../../utils/sectionStatus';
import { SectionCard } from '../SectionCard';
import { SectionHeader } from './SectionHeader';
import { mergeFormValues } from '../../utils/valueMerger';
import {
  getVisibleCards,
  getCardInstances,
  isCardConsideredCompleted,
} from '../../utils/cardUtils';
import { evaluateConditions } from '../../utils/conditions';
import { SectionCardActions } from '../SectionCard/SectionCardActions';
import FormInfo from 'helpers/WillsForm/FormInfo';
import useDictionary from 'lib/hooks/useDictionary';

interface CardSectionProps {
  section: Section;
  sectionIndex: number;
  stepsById: Record<string, StepConfig>;
  completedForms: Record<string, FormInstance[]>;
  firstIncompleteIndex: string | number;
  values: Record<string, unknown>;
  sectionStatus: SectionStatus;
  onSectionClick: (cardOrSectionId: string) => void;
  onSectionEdit: (cardOrSectionId: string, instanceId: string) => void;
  localStorageKey?: string;
  currentStepId?: string;
  currentStep?: StepConfig;
}

export const CardSection: React.FC<CardSectionProps> = ({
  section,
  sectionIndex,
  stepsById,
  completedForms,
  firstIncompleteIndex,
  values,
  sectionStatus,
  onSectionClick,
  onSectionEdit,
  localStorageKey,
  currentStepId,
  currentStep,
}) => {
  // Get merged values for condition evaluation
  const mergedValues = useMemo(() => {
    return mergeFormValues(values, completedForms, localStorageKey);
  }, [values, localStorageKey, completedForms]);

  const { getDictionaryValue } = useDictionary();

  const visibleCards = useMemo(() => {
    return section.cards ? getVisibleCards(section.cards, mergedValues) : [];
  }, [section.cards, mergedValues]);

  const showCharityEmptyState = useMemo(() => {
    if (section.id !== 'section-estate-beneficiaries') return false;
    if (
      sectionStatus !== getDictionaryValue('Complete', 'Complete') &&
      sectionStatus !== getDictionaryValue('InProgress', 'In Progress')
    )
      return false;
    const charities = completedForms['charities-group'] || [];
    const cashGift = completedForms['cash-gift-group'] || [];
    const cashGiftToSomeone = completedForms['cash-gift-to-someone-group'] || [];
    return charities.length === 0 && cashGift.length === 0 && cashGiftToSomeone.length === 0;
  }, [section.id, sectionStatus, getDictionaryValue, completedForms]);
  // Group cards by columnGroup
  const cardsByColumn = useMemo(() => {
    const groups: Record<string, typeof visibleCards> = {};
    const ungrouped: typeof visibleCards = [];

    visibleCards.forEach((card) => {
      if (card.columnGroup) {
        if (!groups[card.columnGroup]) {
          groups[card.columnGroup] = [];
        }
        groups[card.columnGroup].push(card);
      } else {
        ungrouped.push(card);
      }
    });

    return { groups, ungrouped };
  }, [visibleCards]);

  // Check if section should be considered completed
  const isSectionConsideredCompleted = useMemo(() => {
    if (section.considerCompletedIf) {
      return evaluateConditions(section.considerCompletedIf, mergedValues);
    }
    return false;
  }, [section, mergedValues]);

  // Find which card should show the start button
  const startButtonCard = useMemo(() => {
    // If section is considered completed, don't show CTA
    if (isSectionConsideredCompleted) return null;

    if (!visibleCards || visibleCards.length === 0) return null;

    for (let cardIndex = 0; cardIndex < visibleCards.length; cardIndex++) {
      const card = visibleCards[cardIndex];
      const completedInstances = getCardInstances(card, completedForms);
      const showCTA = card.showCTA !== false;
      const isConsideredCompleted = isCardConsideredCompleted(card, mergedValues);

      if (!completedInstances.length && showCTA && !isConsideredCompleted) {
        return { card, cardIndex };
      }
    }
    return null;
  }, [visibleCards, completedForms, mergedValues, isSectionConsideredCompleted]);

  const isStartButtonEnabled = useMemo(() => {
    if (!startButtonCard) return false;

    const cardIdentifier = `${sectionIndex}-${startButtonCard.cardIndex}`;
    const isFirstIncomplete = firstIncompleteIndex === cardIdentifier;
    const completedInstances = getCardInstances(startButtonCard.card, completedForms);

    // Enable only if this is the first incomplete card globally, or if it has completed instances
    return isFirstIncomplete || completedInstances.length > 0;
  }, [startButtonCard, sectionIndex, firstIncompleteIndex, completedForms]);

  // Helper function to render a card
  const renderCard = (card: (typeof visibleCards)[0], cardIndex: number) => {
    const completedInstances = getCardInstances(card, completedForms);
    const cardIdentifier = `${sectionIndex}-${cardIndex}`;
    const isFirstIncomplete =
      firstIncompleteIndex === cardIdentifier && completedInstances.length === 0;
    const isCTAEnabled = isFirstIncomplete || completedInstances.length > 0;

    const cardSteps = card.stepIds
      .map((stepId) => stepsById[stepId])
      .filter((step) => step !== undefined) as StepConfig[];

    const isCharityCard = card.groupKey === 'charities-group' || card.id === 'card-charity';

    return (
      <SectionCard
        key={card.id}
        card={card}
        steps={cardSteps}
        completedForms={completedForms}
        values={values}
        stepsById={stepsById}
        localStorageKey={localStorageKey}
        onEdit={(instanceId) => onSectionEdit(card.id, instanceId)}
        onAdd={() => onSectionClick(card.id)}
        onStart={() => onSectionClick(card.id)}
        isCTAEnabled={isCTAEnabled}
        startCTAText={section.startCTAText}
        isFirstIncomplete={false}
        showCharityEmptyState={isCharityCard ? showCharityEmptyState : undefined}
        onNavigateToCharityStep={
          isCharityCard
            ? () => onSectionClick('step-8-1-estate-beneficiaries-charities')
            : undefined
        }
      />
    );
  };

  const { container, cardsContainer, columnContainer, startButtonWrapper } = TAILWIND_VARIANTS();

  return (
    <div key={section.id} className={container()}>
      {currentStepId === 'step-8-9-estate-beneficiaries-summary' ? (
        <FormInfo title={currentStep?.title} subTitle={currentStep?.subTitle} />
      ) : (
        <SectionHeader title={section.title} status={sectionStatus} />
      )}

      <div className={cardsContainer()}>
        {/* Render grouped cards in columns */}
        {Object.entries(cardsByColumn.groups).map(([columnGroup, cards]) => (
          <div key={columnGroup} className={columnContainer()}>
            {cards
              .filter((card) => {
                const completedInstances = getCardInstances(card, completedForms);
                const showCTA = card.showCTA !== false;
                return completedInstances.length > 0 || showCTA;
              })
              .map((card, cardIndex) => renderCard(card, cardIndex))}
          </div>
        ))}

        {/* Render ungrouped cards (backward compatibility) */}
        {cardsByColumn.ungrouped
          .filter((card) => {
            const completedInstances = getCardInstances(card, completedForms);
            const showCTA = card.showCTA !== false;
            return completedInstances.length > 0 || showCTA;
          })
          .map((card, cardIndex) => renderCard(card, cardIndex))}
      </div>
      {startButtonCard && (
        <div className={startButtonWrapper()}>
          <SectionCardActions
            cardId={startButtonCard.card.id}
            onStart={() => onSectionClick(startButtonCard.card.id)}
            cardTitle={startButtonCard.card.title}
            showStart={true}
            isCTAEnabled={isStartButtonEnabled}
            startCTAText={section.startCTAText}
          />
        </div>
      )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['flex', 'flex-col', 'gap-spacing-spacing-16'],
    cardsContainer: [
      'flex',
      'flex-col',
      'lg:flex-row',
      'justify-start',
      'lg:items-stretch',
      'gap-spacing-spacing-16',
    ],
    columnContainer: ['flex', 'flex-col', 'gap-spacing-spacing-16', 'w-full', 'lg:w-1/3'],
    startButtonWrapper: ['w-full'],
  },
});
